import { HEURES_PAR_SEMAINE, HOURS_PER_MONTH, WEEKS_PER_MONTH } from "./timeCost.js";
/**
 * Axe dirigeant — ce qu'une PME dépense chaque mois en salaires consacrés à des
 * tâches automatisables.
 *
 * Même discipline que les trois volets salarié : **le montant est calculé ici,
 * jamais par le modèle**. Le modèle propose quels postes mesurer et comment les
 * nommer ; il ne décide d'aucun chiffre. Deux dirigeants qui répondent la même
 * chose obtiennent le même montant, aujourd'hui et dans six mois.
 *
 * Ce module est pur : aucune dépendance au DOM, au réseau ni à l'horloge.
 */
/**
 * Les onze secteurs et leur part de masse salariale chargée dans le chiffre
 * d'affaires.
 *
 * **Ces coefficients ne sont dérivés d'aucune source publiée.** Contrairement
 * aux bases d'exposition du volet 1, qui viennent du document de travail 140 de
 * l'OIT et se vérifient, ceux-ci sont des ordres de grandeur. C'est la limite
 * la plus sérieuse de cet axe et elle doit être écrite partout où le montant
 * s'affiche — page de méthode comprise. Écrire « estimation » est exact ;
 * « validé », « certifié » ou « revu par un cabinet » ne le sont pas tant que
 * personne ne l'a fait.
 *
 * Ils pilotent tout : à effectif et chiffre d'affaires identiques, le commerce
 * à 15 % et l'école à 50 % donnent des coûts horaires dans un rapport de plus
 * de trois. Une correction ultérieure se fait ici, en une ligne par secteur,
 * sans toucher à la formule — c'est pour cela qu'ils sont des données et non du
 * code.
 */
export const SECTORS = [
    { code: "COMMERCE", label: "Commerce et distribution", payrollShare: 0.15 },
    { code: "BTP", label: "BTP et construction", payrollShare: 0.3 },
    { code: "CABINET", label: "Cabinet et services professionnels", payrollShare: 0.45 },
    { code: "SANTE", label: "Santé et pharmacie", payrollShare: 0.4 },
    { code: "ECOLE", label: "École et centre de formation", payrollShare: 0.5 },
    { code: "AGENCE", label: "Agence et communication", payrollShare: 0.45 },
    { code: "RESTAURATION", label: "Restauration et hôtellerie", payrollShare: 0.3 },
    { code: "TRANSPORT", label: "Transport et logistique", payrollShare: 0.25 },
    { code: "AGRO", label: "Agroalimentaire et transformation", payrollShare: 0.25 },
    { code: "IMMOBILIER", label: "Immobilier et services financiers", payrollShare: 0.35 },
    { code: "AUTRE", label: "Autre secteur", payrollShare: 0.3 },
];
/** Le secteur servi quand la saisie ne correspond à rien de connu. */
export const DEFAULT_SECTOR = "AUTRE";
const PART_PAR_SECTEUR = new Map(SECTORS.map((s) => [s.code, s.payrollShare]));
export function isSectorCode(valeur) {
    return typeof valeur === "string" && PART_PAR_SECTEUR.has(valeur);
}
/** Chiffre d'affaires mensuel médian retenu par tranche, en FCFA. */
export const REVENUE_MEDIANS = [3e6, 12e6, 32e6, 70e6];
/** Effectif médian retenu par tranche. */
export const STAFF_MEDIANS = [6, 16, 35, 70];
/** Les tranches telles qu'elles sont proposées à l'écran. */
export const REVENUE_BRACKETS = [
    "Moins de 5 millions",
    "5 à 20 millions",
    "20 à 50 millions",
    "Plus de 50 millions",
];
export const STAFF_BRACKETS = ["3 à 9", "10 à 24", "25 à 50", "Plus de 50"];
/** Les quatre volumes horaires hebdomadaires proposés par poste. */
export const WEEKLY_HOUR_STEPS = [
    { label: "Aucune ou presque", hours: 0 },
    { label: "1 à 3 heures", hours: 2 },
    { label: "4 à 10 heures", hours: 6 },
    { label: "Plus de 10 heures", hours: 14 },
];
/**
 * Bornes du coût horaire chargé, en FCFA.
 *
 * Elles existent parce que la formule diverge aux extrêmes : une PME de trois
 * personnes déclarant plus de cinquante millions produirait un coût horaire que
 * personne ne reconnaîtrait. Elles ne corrigent pas l'estimation, elles
 * l'empêchent d'être absurde.
 */
export const HOURLY_COST_MIN = 800;
export const HOURLY_COST_MAX = 6000;
/**
 * Le pas d'arrondi du montant mensuel, **toujours vers le bas**.
 *
 * Un montant arrondi au supérieur se retourne contre le produit à la première
 * lecture attentive. Arrondir à la baisse rend le chiffre défendable : il est
 * au moins celui-là. La ligne « base de calcul » reste affichée dessous, et ne
 * se retire pas pour faire plus percutant.
 */
export const ROUNDING_STEP = 5000;
/**
 * Heures travaillées par semaine — **la constante du volet 2, pas une seconde**.
 *
 * Elle valait 173 ÷ 4,33 = 39,95, déduite « pour ne pas poser une seconde
 * constante ». Le raisonnement était juste et l'application ne l'était plus
 * depuis le 03/09/2026 : ce jour-là, `HEURES_PAR_SEMAINE` a été posée à 40 dans
 * le volet 2, et ce fichier gardait sa propre valeur. Deux noms, deux nombres,
 * une seule grandeur — exactement ce que ce commentaire disait vouloir éviter.
 * On réexporte donc la constante commune ; le nom anglais reste, parce que tout
 * ce module l'emploie.
 */
export const HOURS_PER_WEEK = HEURES_PAR_SEMAINE;
/**
 * Les heures que l'IA peut prendre en charge, par semaine — 03/09/2026.
 *
 * Sortie de `computePayrollDrain` pour être appelable seule : c'est le chiffre
 * de la couverture du rapport dirigeant, et le plafond que ses actions ne
 * doivent pas dépasser. Elle était calculée en un seul endroit, à l'intérieur
 * du drain ; le barème de qualité aurait dû la refaire, et deux calculs de la
 * même chose finissent par diverger.
 *
 * Le pendant salarié est `recuperablesParSemaine` : là, les taux viennent du
 * niveau de la tâche ; ici, du taux propre à chaque poste.
 */
export function heuresAutomatisables(postes) {
    const total = postes.reduce((somme, p) => somme + p.hoursPerWeek * p.auto, 0);
    return Math.round(total * 10) / 10;
}
/**
 * Charges patronales — **la seule valeur non sourcée du chemin secondaire**.
 *
 * Son nom dit ce qu'elle est : un plancher, délibérément bas, en attendant
 * d'être remplacée par le taux réellement applicable. Le produit arrondit tout
 * à la baisse ; sous-estimer les charges va dans le même sens qu'arrondir le
 * montant vers le bas, et ne peut donc pas gonfler le chiffre affiché.
 *
 * Ce qui la distingue des onze coefficients sectoriels : elle relève d'un
 * **texte réglementaire publié** — barème des cotisations sociales et durée
 * légale du travail — et non d'une enquête statistique. Un texte de droit se
 * cite, se date et se vérifie ; c'est autrement plus atteignable qu'un ratio
 * masse salariale sur chiffre d'affaires.
 *
 * Et la voie qui ne l'emploie pas — la masse salariale déclarée, charges
 * comprises — n'a besoin d'aucune valeur de ce genre.
 */
export const CHARGES_PATRONALES_PLANCHER = 0.15;
/** Bornes imposées à un taux d'automatisation proposé par le modèle. */
export const AUTO_RATE_MIN = 0.3;
export const AUTO_RATE_MAX = 0.8;
/** Bornes imposées à un gain proposé par le modèle pour une action. */
export const GAIN_MIN = 0.25;
export const GAIN_MAX = 0.6;
function borner(valeur, min, max) {
    return Math.min(max, Math.max(min, valeur));
}
function estFini(valeur) {
    return typeof valeur === "number" && Number.isFinite(valeur);
}
/**
 * Ramène un taux d'automatisation venu du modèle dans ses bornes.
 *
 * C'est ici que passe la règle « le modèle ne calcule jamais ». Il peut
 * proposer qu'un poste soit automatisable à 70 % ; il ne peut ni descendre sous
 * 30 % — auquel cas le poste n'aurait pas sa place dans la liste — ni monter
 * au-dessus de 80 %, qui reviendrait à promettre la disparition d'une tâche.
 * Une valeur absente ou illisible retombe au milieu de la fourchette.
 */
export function boundAutoRate(valeur) {
    return estFini(valeur) ? borner(valeur, AUTO_RATE_MIN, AUTO_RATE_MAX) : 0.5;
}
/** Même règle pour le gain annoncé d'une action. */
export function boundGain(valeur) {
    return estFini(valeur) ? borner(valeur, GAIN_MIN, GAIN_MAX) : 0.45;
}
/**
 * Le coût horaire chargé, et **d'où il vient**.
 *
 * Trois provenances possibles, et l'écran doit les distinguer parce qu'elles
 * n'ont pas la même valeur de preuve :
 *
 * - `declare` : le dirigeant a corrigé lui-même le coût horaire. C'est le seul
 *   cas où le montant ne dépend d'aucun coefficient — et donc le seul qui
 *   échappe entièrement à la limite énoncée plus haut. C'est pour cela que la
 *   correction est offerte : elle transforme notre estimation en la sienne.
 * - `estime` : déduit du secteur, de l'effectif et du chiffre d'affaires.
 * - `estime_bas` : le chiffre d'affaires n'a pas été donné. On ne le devine
 *   pas — on retient la tranche la plus basse, ce qui produit le coût horaire
 *   le plus faible, donc un montant qui ne peut pas être surestimé. L'écran le
 *   dit, et propose de préciser pour affiner.
 */
export function computeHourlyCost(input) {
    const borne = (valeur, source) => ({
        // Bornée quelle que soit la voie : un dirigeant qui saisit un salaire
        // mensuel là où on attend un coût horaire se trompe d'unité, et le produit
        // ne doit pas afficher son erreur. Et il dit quand la borne a parlé.
        hourlyCost: Math.round(borner(valeur, HOURLY_COST_MIN, HOURLY_COST_MAX)),
        source,
        borne: (valeur < HOURLY_COST_MIN ? "plancher" : valeur > HOURLY_COST_MAX ? "plafond" : null),
    });
    // L'effectif déclaré prime sur la médiane de tranche : quand le dirigeant a
    // donné sa masse salariale, diviser par une médiane rendrait approximatif un
    // calcul qui ne l'est pas.
    const effectif = estFini(input.staffCount) && input.staffCount > 0
        ? input.staffCount
        : (STAFF_MEDIANS[input.staff] ?? STAFF_MEDIANS[0]);
    // 1. Le coût horaire lui-même, pour qui le connaît.
    if (estFini(input.hourlyCostOverride))
        return borne(input.hourlyCostOverride, "declare");
    // 2. La masse salariale chargée. Aucune hypothèse : une division.
    if (estFini(input.payrollMonthly) && input.payrollMonthly > 0) {
        return borne(input.payrollMonthly / (effectif * HOURS_PER_MONTH), "masse_salariale");
    }
    // 3. Le salaire moyen versé, chargé par un taux issu d'un texte publié.
    if (estFini(input.averageSalary) && input.averageSalary > 0) {
        const charge = input.averageSalary * (1 + CHARGES_PATRONALES_PLANCHER);
        return borne(charge / HOURS_PER_MONTH, "salaire_moyen");
    }
    // 4. Le dernier recours, et le seul qui repose sur les onze coefficients.
    const part = PART_PAR_SECTEUR.get(input.sector) ?? PART_PAR_SECTEUR.get(DEFAULT_SECTOR);
    const revenu = input.revenue === null ? REVENUE_MEDIANS[0] : (REVENUE_MEDIANS[input.revenue] ?? REVENUE_MEDIANS[0]);
    const mediane = STAFF_MEDIANS[input.staff] ?? STAFF_MEDIANS[0];
    return borne((revenu * part) / (mediane * HOURS_PER_MONTH), input.revenue === null ? "estime_bas" : "estime");
}
/**
 * Le montant mensuel, poste par poste.
 *
 * `heures hebdomadaires × semaines par mois × coût horaire × taux du poste`,
 * sommé, puis arrondi à la baisse. Les deux constantes de temps sont celles du
 * volet 2 — elles ne sont pas redéclarées ici, sans quoi le produit finirait par
 * dire deux choses différentes du même mois de travail.
 */
export function computePayrollDrain(input) {
    const { hourlyCost, source, borne: hourlyCostBorne } = computeHourlyCost(input);
    const rows = input.tasks.map((task) => {
        const hours = estFini(task.hoursPerWeek) ? Math.max(0, task.hoursPerWeek) : 0;
        const auto = boundAutoRate(task.auto);
        return {
            name: task.name,
            hoursPerWeek: hours,
            auto,
            monthlyCost: hours * WEEKS_PER_MONTH * hourlyCost * auto,
        };
    });
    const brut = rows.reduce((somme, row) => somme + row.monthlyCost, 0);
    const monthly = Math.floor(brut / ROUNDING_STEP) * ROUNDING_STEP;
    /*
      L'équivalent temps plein porte sur la part **automatisable** des heures, et
      non sur les heures brutes.
  
      Dire « soixante-deux heures par semaine, soit 1,55 personne » surestimerait :
      ces soixante-deux heures ne sont pas automatisables en entier, elles le sont
      à hauteur du taux de chaque poste. Le chiffre affiché est donc le plus bas
      des deux — même parti pris que l'arrondi vers le bas.
    */
    const automatableHours = heuresAutomatisables(rows);
    return {
        hourlyCost,
        hourlyCostSource: source,
        hourlyCostBorne,
        rows,
        // Le classement sert l'écran — « vos trois plus grosses fuites » — et il
        // est calculé ici pour que deux écrans ne puissent pas trier différemment.
        ranked: [...rows].sort((a, b) => b.monthlyCost - a.monthlyCost),
        weeklyHours: rows.reduce((somme, row) => somme + row.hoursPerWeek, 0),
        automatableHours,
        fullTimeEquivalent: Math.round((automatableHours / HOURS_PER_WEEK) * 10) / 10,
        monthly,
        yearly: monthly * 12,
        scoringVersion: payrollVersion,
    };
}
/**
 * Version du barème dirigeant, distincte de celle du volet salarié.
 *
 * Les deux axes évoluent séparément : corriger un coefficient de masse
 * salariale ne doit pas invalider les indices d'exposition déjà rendus, et
 * inversement. Toute modification d'une valeur normative de ce fichier —
 * coefficient, borne, pas d'arrondi — s'incrémente ici et se stocke avec le
 * résultat.
 *
 * `payroll-1.2.0` le 03/09/2026 : `HOURS_PER_WEEK` passe de 39,95 — déduite de
 * 173 ÷ 4,33 — à la constante commune du volet 2, posée à 40 le même jour. La
 * règle ci-dessus l'exigeait et l'incrément a d'abord été oublié : la semaine
 * est bien une valeur normative de ce fichier, puisque l'équivalent temps plein
 * s'y divise. L'écart est un écart d'arrondi, pas de barème — pour la quasi-
 * totalité des entreprises le chiffre est identique — mais il déplace la
 * frontière : à 82 heures automatisables, « 2,1 personnes » devient « 2,0 ».
 * C'est le chiffre mis en avant sur la couverture du rapport à 15 000 F.
 */
export const payrollVersion = "payroll-1.4.0";
/**
 * Les tâches proposées par le modèle, ramenées dans les clous.
 *
 * Rend `null` plutôt qu'une liste partielle quand le compte n'y est pas : un
 * questionnaire de trois postes ne mesure pas la même chose qu'un de sept, et
 * servir un montant calculé sur une liste amputée serait servir un faux
 * chiffre. L'appelant retombe alors sur sa banque de postes.
 */
export function boundTasks(brut, min = 5, max = 9) {
    if (!Array.isArray(brut))
        return null;
    const propres = brut
        .map((entree) => {
        const objet = (entree ?? {});
        const name = typeof objet.name === "string" ? objet.name.trim() : "";
        if (name.length < 5 || name.length > 90)
            return null;
        return { name, auto: boundAutoRate(objet.auto), hoursPerWeek: 0 };
    })
        .filter((t) => t !== null);
    return propres.length >= min && propres.length <= max ? propres : null;
}
/**
 * Les équivalents parlants du montant annuel.
 *
 * « 380 000 F par mois » est exact et ne se ressent pas ; « chaque année, le
 * salaire chargé d'un commercial expérimenté » se ressent. La banque vient de
 * la référence fonctionnelle du kit d'origine, et elle est ordonnée du plus
 * gros au plus petit : on décrit le montant avec le plus grand objet qui y
 * tient au moins une fois.
 *
 * Ce sont des ordres de grandeur assumés — un fourgon n'a pas un prix unique —
 * mais l'équivalence ne participe à aucun calcul : elle traduit un montant déjà
 * arrondi à la baisse, elle ne le produit pas. La règle « pas de faux
 * signaux » vaut ici comme ailleurs : l'écran affiche toujours le montant en
 * francs à côté, jamais l'image seule.
 */
export const YEARLY_EQUIVALENTS = [
    { value: 14e6, one: "un véhicule utilitaire neuf", many: "véhicules utilitaires neufs" },
    {
        value: 6e6,
        one: "le salaire annuel chargé d'un commercial expérimenté",
        many: "salaires annuels chargés de commerciaux expérimentés",
    },
    { value: 3.6e6, one: "une année de loyer pour vos bureaux", many: "années de loyer pour vos bureaux" },
    // « Un mois de trésorerie d'avance » valait 1 200 000 F pour toute
    // entreprise : une image sans assise, que rien ne relie à celle du lecteur.
    // Retirée le 06/10/2026 ; sous trois millions six, le palier calculé sur le
    // coût horaire (`SALAIRE_MENSUEL`) prend le relais.
];
/**
 * Le dernier palier, dérivé du coût horaire retenu — 08/09/2026.
 *
 * Il valait 300 000 F en dur, et se contredisait avec la ligne du dessus dans
 * le rapport du 08/09 : « Base : 800 F de l'heure, chargés » puis « 420 000 F
 * — l'équivalent d'un mois de salaire d'un employé ». À 800 F de l'heure un
 * employé coûte environ 138 000 F par mois, donc 420 000 F en valent trois, et
 * le document affirmait un et un seul. Le lecteur qui pose les deux chiffres
 * côte à côte trouve un facteur trois dans un encadré de cinq lignes.
 *
 * Trois cent mille francs par mois n'est d'ailleurs pas « un employé » dans la
 * zone : c'est un salaire de cadre. Le palier se calcule donc sur le coût
 * horaire réellement utilisé — le plancher sectoriel quand rien n'est déclaré,
 * le coût du dirigeant quand il l'a transmis — et l'image dit alors la même
 * chose que la base, par construction et quel que soit le coût.
 */
export const SALAIRE_MENSUEL = {
    one: "un mois de salaire d'un employé",
    many: "mois de salaire d'un employé",
};
/** Ce que coûte par mois, chargé, un employé payé au coût horaire retenu. */
export function coutMensuelEmploye(hourlyCost) {
    return hourlyCost * HOURS_PER_MONTH;
}
/**
 * La phrase qui traduit un montant annuel, ou `null` s'il est trop petit pour
 * qu'une image soit honnête. L'appelant n'affiche alors que les francs :
 * mieux vaut pas d'image qu'une image forcée.
 */
export function speakingEquivalent(yearly, hourlyCost) {
    if (!Number.isFinite(yearly) || yearly <= 0)
        return null;
    for (const equiv of YEARLY_EQUIVALENTS) {
        const n = Math.floor(yearly / equiv.value);
        if (n === 1)
            return equiv.one;
        if (n > 1)
            return `${n} ${equiv.many}`;
    }
    // Le dernier palier, calculé sur le coût horaire de ce rapport plutôt que
    // posé en dur : voir `SALAIRE_MENSUEL`.
    const mois = coutMensuelEmploye(hourlyCost);
    if (!Number.isFinite(mois) || mois <= 0)
        return null;
    const n = Math.floor(yearly / mois);
    if (n === 1)
        return SALAIRE_MENSUEL.one;
    if (n > 1)
        return `${n} ${SALAIRE_MENSUEL.many}`;
    return null;
}
/**
 * L'équivalent précédé de sa préposition contractée : « d'un véhicule », « du
 * salaire annuel chargé », « de 2 salaires ».
 *
 * L'appelant écrivait « l'équivalent de {image} » et le français se brisait
 * sur la moitié de la banque : « de un mois de salaire », « de le salaire
 * annuel » — vu à l'œil sur le PDF payé, présent aussi à l'écran. La
 * contraction dépend du premier mot de l'image ; elle appartient donc au
 * module qui possède la banque, pas à chaque écran.
 */
export function speakingEquivalentAvecDe(yearly, hourlyCost) {
    const image = speakingEquivalent(yearly, hourlyCost);
    if (image === null)
        return null;
    if (/^une? /.test(image))
        return `d'${image}`;
    if (image.startsWith("le "))
        return `du ${image.slice(3)}`;
    if (image.startsWith("la "))
        return `de la ${image.slice(3)}`;
    return `de ${image}`;
}
